/**
 * Analytics ID Validators
 *
 * Security utilities for validating analytics tracking IDs before injection.
 * Prevents XSS attacks via malicious analytics ID values in dangerouslySetInnerHTML.
 *
 * @see https://github.com/owner/repo/issues/53
 */

/**
 * Valid Google Analytics ID formats:
 * - GA4: G-XXXXXXXX (Measurement ID)
 * - Universal Analytics: UA-XXXXXXX-X
 * - YouTube Analytics: YT-XXXXXXX
 * - Google Ads: MO-XXXXXXX (Modifier ID)
 * - AW-XXXXXXX (Google Ads Conversion)
 * - DC-XXXXXXX (DoubleClick)
 * - GT-XXXXXXX (Google Tag Manager Container)
 */
const GOOGLE_ANALYTICS_PATTERN = /^(G|UA|YT|MO|AW|DC|GT)-[A-Za-z0-9-]+$/;

/**
 * Facebook Pixel ID format: Numeric only (typically 15-16 digits)
 */
const FACEBOOK_PIXEL_PATTERN = /^\d{10,20}$/;

/**
 * Generic tracking ID pattern for other analytics providers
 * Allows alphanumeric characters, hyphens, and underscores only
 */
const GENERIC_TRACKING_ID_PATTERN = /^[A-Za-z0-9_-]{1,50}$/;

/**
 * Characters that could enable script injection if present in analytics IDs
 */
const DANGEROUS_CHARS_PATTERN = /[<>'"`;(){}[\]\\]/;

/**
 * Validates a Google Analytics ID (GA4, UA, etc.)
 *
 * @param id - The analytics ID to validate
 * @returns true if the ID matches valid Google Analytics format
 *
 * @example
 * isValidGoogleAnalyticsId('G-ABC123XYZ')  // true
 * isValidGoogleAnalyticsId('UA-12345678-1')  // true
 * isValidGoogleAnalyticsId('<script>alert(1)</script>')  // false
 */
export function isValidGoogleAnalyticsId(id: string | undefined | null): boolean {
  if (!id || typeof id !== 'string') {
    return false;
  }

  const trimmedId = id.trim();

  // Check for dangerous characters first
  if (DANGEROUS_CHARS_PATTERN.test(trimmedId)) {
    return false;
  }

  return GOOGLE_ANALYTICS_PATTERN.test(trimmedId);
}

/**
 * Validates a Facebook Pixel ID
 *
 * @param id - The pixel ID to validate
 * @returns true if the ID is a valid Facebook Pixel ID (numeric, 10-20 digits)
 *
 * @example
 * isValidFacebookPixelId('1234567890123456')  // true
 * isValidFacebookPixelId('abc123')  // false
 * isValidFacebookPixelId("'; DROP TABLE users;--")  // false
 */
export function isValidFacebookPixelId(id: string | undefined | null): boolean {
  if (!id || typeof id !== 'string') {
    return false;
  }

  const trimmedId = id.trim();

  // Check for dangerous characters first
  if (DANGEROUS_CHARS_PATTERN.test(trimmedId)) {
    return false;
  }

  return FACEBOOK_PIXEL_PATTERN.test(trimmedId);
}

/**
 * Validates a generic tracking ID for other analytics providers
 *
 * @param id - The tracking ID to validate
 * @returns true if the ID contains only safe characters
 *
 * @example
 * isValidTrackingId('TRACK-123-ABC')  // true
 * isValidTrackingId('my_analytics_id')  // true
 * isValidTrackingId('<img onerror=alert(1)>')  // false
 */
export function isValidTrackingId(id: string | undefined | null): boolean {
  if (!id || typeof id !== 'string') {
    return false;
  }

  const trimmedId = id.trim();

  // Check for dangerous characters first
  if (DANGEROUS_CHARS_PATTERN.test(trimmedId)) {
    return false;
  }

  return GENERIC_TRACKING_ID_PATTERN.test(trimmedId);
}

/**
 * Sanitizes a string for safe use in script content
 * Returns the original string if safe, or null if potentially dangerous
 *
 * @param value - The value to sanitize
 * @returns The sanitized value or null if unsafe
 */
export function sanitizeForScript(value: string | undefined | null): string | null {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  // Reject if contains dangerous characters
  if (DANGEROUS_CHARS_PATTERN.test(trimmed)) {
    console.warn(
      '[Analytics Security] Rejected potentially dangerous value:',
      trimmed.substring(0, 50)
    );
    return null;
  }

  return trimmed;
}

/**
 * Validates and returns a Google Analytics ID, or null if invalid
 *
 * @param id - The analytics ID to validate
 * @returns The validated ID or null
 */
export function getValidatedGoogleAnalyticsId(id: string | undefined | null): string | null {
  if (isValidGoogleAnalyticsId(id)) {
    return id!.trim();
  }

  if (id) {
    console.warn(
      '[Analytics Security] Invalid Google Analytics ID format:',
      id.substring(0, 30)
    );
  }

  return null;
}

/**
 * Validates and returns a Facebook Pixel ID, or null if invalid
 *
 * @param id - The pixel ID to validate
 * @returns The validated ID or null
 */
export function getValidatedFacebookPixelId(id: string | undefined | null): string | null {
  if (isValidFacebookPixelId(id)) {
    return id!.trim();
  }

  if (id) {
    console.warn(
      '[Analytics Security] Invalid Facebook Pixel ID format:',
      id.substring(0, 30)
    );
  }

  return null;
}
